import React, { useState } from 'react';
import { Calendar, X } from 'lucide-react';
import { Button } from './Button';
interface TrainingCycleAlignmentModalProps {
  selectedDate: string;
  nextWorkoutDate: string;
  alignedDate: string;
  onStartNextWorkout: () => void;
  onAlignToDay1: () => void;
  onClose: () => void;
  preselectedOption?: 'next' | 'align';
}
export function TrainingCycleAlignmentModal({
  selectedDate,
  nextWorkoutDate,
  alignedDate,
  onStartNextWorkout,
  onAlignToDay1,
  onClose,
  preselectedOption
}: TrainingCycleAlignmentModalProps) {
  const [selectedOption, setSelectedOption] = useState<'next' | 'align' | null>(preselectedOption || null);
  const handleConfirm = () => {
    if (selectedOption === 'next') {
      onStartNextWorkout();
    } else if (selectedOption === 'align') {
      onAlignToDay1();
    }
  };
  return <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-in fade-in duration-200" data-id="element-385">
      <div className="bg-white w-full max-w-sm rounded-[2.5rem] p-6 shadow-2xl animate-in zoom-in-95 duration-200 relative" data-id="element-386">
        <button onClick={onClose} className="absolute top-4 right-4 p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-full transition-colors" data-id="element-387">
          <X size={20} data-id="element-388" />
        </button>

        <div className="w-12 h-12 bg-blue-50 text-blue-500 rounded-full flex items-center justify-center mb-5 mx-auto" data-id="element-389">
          <Calendar size={24} data-id="element-390" />
        </div>

        <div className="text-center mb-6" data-id="element-391">
          <h2 className="text-2xl font-extrabold text-slate-800 mb-3" data-id="element-392">
            How should we start your program?
          </h2>
          <p className="text-slate-600 font-medium text-sm leading-relaxed" data-id="element-393">
            You selected{' '}
            <span className="font-bold text-slate-800" data-id="element-394">{selectedDate}</span> as
            your start date, but your training cycle begins on Tuesday.
          </p>
          <p className="text-slate-600 font-medium text-sm leading-relaxed mt-2" data-id="element-395">
            You can either start with the next available workout day, or align
            your program so Day 1 begins on the first day of your training
            cycle.
          </p>
        </div>

        <div className="space-y-3 mb-6" data-id="element-396">
          {/* Option A - Recommended */}
          <button onClick={() => setSelectedOption('next')} className={`w-full text-left p-4 rounded-2xl border-2 transition-all duration-200 ${selectedOption === 'next' ? 'bg-emerald-50/50 border-emerald-500 shadow-sm' : 'bg-white border-slate-200 hover:border-slate-300'}`} data-id="element-397">
            <div className="flex items-start justify-between mb-2" data-id="element-398">
              <div className="flex items-center gap-2" data-id="element-399">
                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${selectedOption === 'next' ? 'border-emerald-500 bg-emerald-500' : 'border-slate-300'}`} data-id="element-400">
                  {selectedOption === 'next' && <div className="w-2 h-2 bg-white rounded-full" data-id="element-401" />}
                </div>
                <span className="font-extrabold text-slate-800 text-base" data-id="element-402">
                  Start with {nextWorkoutDate}
                </span>
              </div>
              <span className="text-xs font-bold text-emerald-600 bg-emerald-50 px-2 py-1 rounded-full" data-id="element-403">
                Recommended
              </span>
            </div>
            <p className="text-sm text-slate-500 font-medium ml-7" data-id="element-404">
              This makes your next available workout the first day of your
              program.
            </p>
          </button>

          {/* Option B */}
          <button onClick={() => setSelectedOption('align')} className={`w-full text-left p-4 rounded-2xl border-2 transition-all duration-200 ${selectedOption === 'align' ? 'bg-emerald-50/50 border-emerald-500 shadow-sm' : 'bg-white border-slate-200 hover:border-slate-300'}`} data-id="element-405">
            <div className="flex items-start gap-2 mb-2" data-id="element-406">
              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors mt-0.5 ${selectedOption === 'align' ? 'border-emerald-500 bg-emerald-500' : 'border-slate-300'}`} data-id="element-407">
                {selectedOption === 'align' && <div className="w-2 h-2 bg-white rounded-full" data-id="element-408" />}
              </div>
              <div data-id="element-409">
                <span className="font-extrabold text-slate-800 text-base block" data-id="element-410">
                  Align Day 1 to {alignedDate}
                </span>
                <p className="text-sm text-slate-500 font-medium mt-1" data-id="element-411">
                  This keeps your training cycle aligned from Day 1 each week.
                </p>
              </div>
            </div>
          </button>
        </div>

        <Button variant="primary" fullWidth onClick={handleConfirm} disabled={!selectedOption} data-id="element-412">
          Confirm
        </Button>
      </div>
    </div>;
}